import React, {FormEvent} from 'react';
import Toggle from "components/Form/Toggle";
import {FieldArray, FieldArrayRenderProps, FormikHelpers} from "formik";
import Selector from "components/Form/Selector";
import Field from "components/Form/Field";
import Button from "components/Button";
import {PlusIcon, TrashIcon} from "@heroicons/react/solid";
import {ExhibitFilter, InteractionFilter as InteractionFilterSpec} from "types/AudienceCriteria";
import {useOperator} from "context/OperatorContext";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import IconName from "types/IconName";
import {setFilterDates} from './Filter';
import {Item} from 'components/Form/Selector/Selector';

type Props = {
  formik: FormikHelpers<any>
  groupIndex: number
  filter?: InteractionFilterSpec | null
}

const exhibitFilterTemplate: ExhibitFilter = {
  mode: 'ever',
  dates: [],
  exhibit_id: 0
}

function InteractionFilter({formik, groupIndex, filter}: Props) {
  const {termFor} = useOperator()

  const {data: exhibits, isLoading: exhibitsLoading} = useQuery(
    ['exhibits.all', {page: 1, perPage: 1000}],
    () => ngage.exhibits.all({page: 1, per_page: 1000})
  )

  const selectableExhibits = exhibits?.data.map(exhibit => ({
    id: exhibit.id,
    name: exhibit.name,
    description: exhibit.type.name,
    icon: IconName.LIBRARY,
  })) || []

  // Handle toggling this filter on and off
  const handleToggle = (checked: boolean): void => {
    void formik.setFieldValue(
      `criteria.groups.${groupIndex}.filters.interaction`,
      checked ? {exhibits: [{...exhibitFilterTemplate}]} : undefined
    )
  }

  // Handle selecting an exhibit
  const handleSelectExhibit = (item: Item, exhibitIndex: number): void => {
    void formik.setFieldValue(
      `criteria.groups.${groupIndex}.filters.interaction.exhibits.${exhibitIndex}.exhibit_id`,
      item.id
    )
  }

  // Handling changing the date mode
  const handleChangeMode = (e: FormEvent<HTMLSelectElement>, exhibitFilter: ExhibitFilter, exhibitIndex: number) => {
    void formik.setFieldValue(
      `criteria.groups.${groupIndex}.filters.interaction.exhibits.${exhibitIndex}.mode`,
      e.currentTarget.value
    )

    setFilterDates(
      formik,
      `criteria.groups.${groupIndex}.filters.interaction.exhibits.${exhibitIndex}.dates`,
      e.currentTarget.value,
      exhibitFilter.dates || []
    )
  }

  return (
    <div className="mt-8">
      <div className="flex items-center">
        <Toggle
          label="Interacted with"
          checked={!!filter}
          onChange={handleToggle}
          loading={exhibitsLoading}
          disabled={exhibitsLoading}
        />

        {!filter && <div className="font-medium text-gray-700">{termFor('exhibits')}...</div>}
      </div>

      <FieldArray name={`criteria.groups.${groupIndex}.filters.interaction.exhibits`}>
        {({push, remove}: FieldArrayRenderProps) => (
          <div>
            <ul className="mt-2 pl-14">
              {filter?.exhibits.map((exhibitFilter, exhibitIndex) => (
                <li key={exhibitIndex}>
                  <div className="flex justify-between sm:items-center">
                    <div className="sm:flex sm:items-center">
                      <div>
                        <Selector
                          isLoading={exhibitsLoading}
                          items={selectableExhibits}
                          selectedId={exhibitFilter.exhibit_id}
                          onChange={item => handleSelectExhibit(item, exhibitIndex)}
                          required
                        />
                      </div>

                      <div className="w-32 mt-2 sm:mt-0 sm:ml-3">
                        <Field
                          as="select"
                          name={`criteria.groups.${groupIndex}.filters.interaction.exhibits.${exhibitIndex}.mode`}
                          onChange={(event: React.FormEvent<HTMLSelectElement>) => handleChangeMode(event, exhibitFilter, exhibitIndex)}
                        >
                          <option value="ever">ever</option>
                          <option value="between">between</option>
                          <option value="before">before</option>
                          <option value="after">after</option>
                        </Field>
                      </div>

                      <div className="mt-2 sm:mt-0 sm:ml-3">
                        {exhibitFilter.mode !== 'ever' &&
                          <Field
                            type="date"
                            name={`criteria.groups.${groupIndex}.filters.interaction.exhibits.${exhibitIndex}.dates.0`}
                          />
                        }
                      </div>

                      <div className="sm:ml-3 sm:flex sm:items-center">
                        {exhibitFilter.mode === 'between' &&
                          <>
                            <span className="ml-3 sm:ml-0 sm:mr-3">and</span>
                            <Field
                              type="date"
                              name={`criteria.groups.${groupIndex}.filters.interaction.exhibits.${exhibitIndex}.dates.1`}
                            />
                          </>
                        }
                      </div>
                    </div>

                    {(filter?.exhibits?.length || 0) > 1 &&
                      <div>
                        <Button
                          color={Button.colors.WHITE}
                          size={Button.sizes.TINY}
                          icon={TrashIcon}
                          onClick={() => remove(exhibitIndex)}
                        />
                      </div>
                    }
                  </div>

                  {exhibitIndex !== (filter?.exhibits.length || 0) - 1 &&
                    <div className="ml-3 my-4 sm:ml-0 sm:my-1 uppercase sm:normal-case">and</div>
                  }
                </li>
              ))}
            </ul>

            {filter &&
              <div className="mt-4 pl-14">
                <Button
                  color={Button.colors.SECONDARY}
                  icon={PlusIcon}
                  size={Button.sizes.TINY}
                  onClick={() => push({...exhibitFilterTemplate})}
                >
                  Add Interaction
                </Button>
              </div>
            }
          </div>
        )}
      </FieldArray>
    </div>
  );
}

export default InteractionFilter;